/**
 * Nassau with presses — the clubhouse side match.
 *
 * Played after the round, off two cards that are already in the app. You pick
 * two players, say who gives how many strokes, and walk the holes: the point is
 * the telling, not the total. "Josh wins the 4th — two up. Press?"
 *
 * THE ONLY THING STORED IS THE PRESS DECISIONS. Every bet, every margin and
 * every dollar is derived from the two cards plus that list, exactly as the
 * tournament derives its leaderboard from gross strokes alone. Correcting a
 * score, or changing the strokes given, therefore repairs the whole match
 * retroactively instead of leaving a stale ledger behind.
 *
 * The rules this implements, which are the group's and not universal:
 *
 *   - Three bets to begin with: the front, the back, and the total.
 *   - You press because you ARE down an even number of holes — two, four, six.
 *     Three down is not a pressing position. The question is asked on the tee,
 *     off where the bet already stands: lose the first two and you are asked
 *     walking to the 3rd, and the press covers the 3rd onward.
 *   - It is OFFERED, never automatic. Turning it down at a given margin does
 *     not ask again until the margin moves.
 *   - A press is its own bet, for the same money.
 *   - A press can itself be pressed. That is where the money compounds.
 *   - A press settles with its parent. A press of the front is done at nine
 *     even though the total rolls on.
 */

import { allocatePops } from './handicap'
import type { Hole } from './types'

export type NassauSegment = 'front' | 'back' | 'total'

export interface NassauBet {
  id: string
  label: string
  segment: NassauSegment
  parentId: string | null
  /** 0 is an original bet, 1 its press, 2 a press on that press. */
  depth: number
  /** Positions into the supplied hole list, inclusive. */
  startIndex: number
  endIndex: number
}

export interface PressDecision {
  parentId: string
  /** Position of the hole being walked to when the question was asked. */
  atIndex: number
  accepted: boolean
}

export interface PressOffer {
  parentId: string
  parentLabel: string
  atIndex: number
  holeNo: number
  /** The side that is down, and therefore the side doing the pressing. */
  trailing: 'A' | 'B'
  trailingName: string
}

export interface BetStanding {
  bet: NassauBet
  /** Holes up for A. Negative means B leads. */
  up: number
  played: number
  remaining: number
  /** Past its last hole. */
  closed: boolean
  /** Cannot change hands any more, whether or not holes remain. */
  decided: boolean
  /** "3 UP" | "AS" | "2 DN", always from A's point of view. */
  status: string
  /** +1 A, -1 B, 0 halved, null still open. */
  winner: number | null
}

export interface NassauStep {
  index: number
  /**
   * Where this hole sits in the match, 1-based.
   *
   * Not the same as `holeNo` once a match spans two nines: the trip plays the
   * front twice in a day, so the card says hole 3 twice and "the 3rd" would
   * name two different holes. The match is played in sequence, so the sequence
   * is what gets narrated.
   */
  position: number
  holeNo: number
  par: number
  strokeIndex: number
  popsA: number
  popsB: number
  grossA: number | null
  grossB: number | null
  netA: number | null
  netB: number | null
  /** +1 A won the hole, -1 B, 0 halved, null not played. */
  outcome: number | null
  /** How every bet running through this hole stood once it was done. */
  standings: BetStanding[]
  narration: string
  /** Asked on this tee, before the hole was played, answered or not. */
  offers: PressOffer[]
}

export interface NassauLedger {
  aBets: number
  bBets: number
  halved: number
  open: number
  /** Positive means A collects. */
  net: number
}

export interface NassauWalk {
  bets: NassauBet[]
  steps: NassauStep[]
  standings: BetStanding[]
  ledger: NassauLedger
  /** Raised but not yet answered — what the UI has to ask about. */
  pending: PressOffer[]
  /** How many holes of the card actually had both scores in. */
  playedThrough: number
}

export interface NassauInput {
  holes: Hole[]
  grossA: (number | null)[]
  grossB: (number | null)[]
  /** Strokes A gives B. Negative means B gives A. */
  strokes: number
  amountPerBet: number
  decisions: PressDecision[]
  nameA: string
  nameB: string
}

const label = (up: number): string =>
  up === 0 ? 'AS' : `${Math.abs(up)} ${up > 0 ? 'UP' : 'DN'}`

const ordinal = (n: number): string => {
  const teen = n % 100
  if (teen >= 11 && teen <= 13) return `${n}th`
  return `${n}${['th', 'st', 'nd', 'rd'][n % 10] ?? 'th'}`
}

function pressLabel(parent: NassauBet): string {
  const base = parent.segment === 'total' ? 'Total' : parent.segment === 'front' ? 'Front' : 'Back'
  const depth = parent.depth + 1
  if (depth === 1) return `${base} press`
  if (depth === 2) return `${base} re-press`
  return `${base} press ${depth}`
}

/**
 * The bets that exist before anyone presses.
 *
 * Anything shorter than a full eighteen is one match rather than a Nassau —
 * splitting a single nine into halves invents a bet nobody agreed to.
 */
function baseBets(holeCount: number): NassauBet[] {
  if (holeCount < 18) {
    return [
      {
        id: 'total',
        label: 'The match',
        segment: 'total',
        parentId: null,
        depth: 0,
        startIndex: 0,
        endIndex: holeCount - 1,
      },
    ]
  }
  const half = Math.floor(holeCount / 2)
  return [
    {
      id: 'front',
      label: 'Front',
      segment: 'front',
      parentId: null,
      depth: 0,
      startIndex: 0,
      endIndex: half - 1,
    },
    {
      id: 'back',
      label: 'Back',
      segment: 'back',
      parentId: null,
      depth: 0,
      startIndex: half,
      endIndex: holeCount - 1,
    },
    {
      id: 'total',
      label: 'Total',
      segment: 'total',
      parentId: null,
      depth: 0,
      startIndex: 0,
      endIndex: holeCount - 1,
    },
  ]
}

export function runNassau(input: NassauInput): NassauWalk {
  const { holes, grossA, grossB, strokes, amountPerBet, decisions, nameA, nameB } = input
  const n = holes.length

  // Whoever is getting strokes takes them on the hardest holes of the set being
  // played, which is what the card's stroke index already ranks.
  const popsA = strokes < 0 ? allocatePops(holes, -strokes) : new Array<number>(n).fill(0)
  const popsB = strokes > 0 ? allocatePops(holes, strokes) : new Array<number>(n).fill(0)

  const bets = baseBets(n)
  const up = new Map<string, number>()
  const played = new Map<string, number>()
  for (const b of bets) {
    up.set(b.id, 0)
    played.set(b.id, 0)
  }

  const decisionFor = (parentId: string, atIndex: number) =>
    decisions.find((d) => d.parentId === parentId && d.atIndex === atIndex)

  const steps: NassauStep[] = []
  const pending: PressOffer[] = []
  /**
   * Margin a press was turned down at, per bet. Cleared the moment the
   * margin moves off that value — see the update loop below — so that
   * coming back to the same number later is a fresh question, not a remembered no.
   */
  const declined = new Map<string, number>()
  /** Margin a bet was standing at the moment it became decided, per bet id. */
  const closeoutMargin = new Map<string, number>()
  let playedThrough = 0

  /**
   * What is left is measured in holes still to be SCORED, not holes still to be
   * walked past. A card abandoned on the 4th leaves the front two up with six
   * to play, which is not a win — reading the position on the card instead
   * would quietly pay out a match nobody finished.
   */
  const standingOf = (b: NassauBet): BetStanding => {
    const u = up.get(b.id) ?? 0
    const p = played.get(b.id) ?? 0
    const remaining = Math.max(0, b.endIndex - b.startIndex + 1 - p)
    const closed = remaining === 0
    const decided = Math.abs(u) > remaining
    // The margin at closeout, not the live tally: once decided, the holes
    // still on the card keep getting played and keep moving `up`, but that
    // motion cannot change who wins the bet (|up_then| - remaining is the
    // invariant that makes it decided in the first place). Freezing here the
    // first time a bet is seen decided is what lets a 5&4 stay a 5&4 instead
    // of drifting toward "1 DN" as the loser takes the dead holes back.
    const frozen = closeoutMargin.get(b.id)
    if (decided && frozen === undefined) closeoutMargin.set(b.id, u)
    const displayUp = closeoutMargin.get(b.id) ?? u
    return {
      bet: b,
      up: u,
      played: p,
      remaining,
      closed,
      decided,
      status: label(displayUp),
      winner: closed || decided ? Math.sign(u) : null,
    }
  }

  for (let i = 0; i < n; i++) {
    const hole = holes[i]!
    const ga = grossA[i] ?? null
    const gb = grossB[i] ?? null
    const na = ga == null ? null : ga - (popsA[i] ?? 0)
    const nb = gb == null ? null : gb - (popsB[i] ?? 0)

    const outcome = na == null || nb == null ? null : na < nb ? 1 : na > nb ? -1 : 0
    if (outcome !== null) playedThrough = i + 1

    // Bets live on this hole, including presses created on an earlier one.
    const liveNow = () => bets.filter((b) => i >= b.startIndex && i <= b.endIndex)

    /*
     * Offers come BEFORE the hole is played, off where the bet already stands.
     *
     * You press because you ARE two down, not because you just went two down,
     * and you say so on the tee — lose the first two and the question is asked
     * walking to the 3rd, so the press covers the 3rd onward. Asking after the
     * hole instead would put the question a hole late and make the press cover
     * the wrong holes.
     *
     * Sitting at the same margin does not re-ask; going further down does, and
     * so does coming back to two after climbing out. Otherwise a player who is
     * three down for six holes gets the same question six times.
     */
    const offers: PressOffer[] = []
    for (const b of liveNow()) {
      // Nothing to press once the bet can no longer change hands — pressing
      // it would create a real, same-money side bet on a result that is
      // already locked in, which is not a bet anybody meant to offer.
      const standingNow = standingOf(b)
      if (standingNow.decided || standingNow.closed) continue
      /*
       * The total is not pressable on the front nine — Scott's ruling, and
       * the way the game has always been played at this table: on the front
       * you may press the front; once you turn, the back and the total are
       * both live. Pressing the total on the 4th is betting eighteen holes
       * on nine holes of evidence, which is not what the men agreed to when
       * they struck the match.
       *
       * A press of a press inherits its parent's segment, so this covers the
       * deep ones too — a total press cannot be born before the turn at any
       * depth.
       */
      if (b.segment === 'total' && n >= 18 && i < Math.floor(n / 2)) continue
      const margin = up.get(b.id) ?? 0
      const down = Math.abs(margin)
      // Two down, four down, six down. Three down is not a pressing position —
      // you climb back to an even number of holes first, or you do not press.
      if (down < 2 || down % 2 !== 0) continue
      if (bets.some((x) => x.parentId === b.id)) continue
      if (declined.get(b.id) === margin) continue

      const trailing: 'A' | 'B' = margin > 0 ? 'B' : 'A'
      const offer: PressOffer = {
        parentId: b.id,
        parentLabel: b.label,
        atIndex: i,
        holeNo: hole.number,
        trailing,
        trailingName: trailing === 'A' ? nameA : nameB,
      }
      offers.push(offer)

      const answer = decisionFor(b.id, i)
      if (!answer) {
        pending.push(offer)
      } else if (answer.accepted) {
        const child: NassauBet = {
          id: `${b.id}+${i}`,
          label: pressLabel(b),
          segment: b.segment,
          parentId: b.id,
          depth: b.depth + 1,
          // From this hole, the one being walked to.
          startIndex: i,
          endIndex: b.endIndex,
        }
        bets.push(child)
        up.set(child.id, 0)
        played.set(child.id, 0)
      } else {
        declined.set(b.id, margin)
      }
    }

    if (outcome !== null) {
      for (const b of liveNow()) {
        const nextUp = (up.get(b.id) ?? 0) + outcome
        up.set(b.id, nextUp)
        played.set(b.id, (played.get(b.id) ?? 0) + 1)
        // A decline only covers the margin it was said at. The moment the
        // hole moves the margin away from that number, the no no longer
        // applies — so a later return to the same margin is a new question.
        if (declined.get(b.id) !== undefined && declined.get(b.id) !== nextUp) {
          declined.delete(b.id)
        }
      }
    }

    const standings = bets
      .filter((b) => i >= b.startIndex && i <= b.endIndex)
      .map((b) => standingOf(b))

    const position = i + 1
    const winnerName = outcome === 1 ? nameA : outcome === -1 ? nameB : null
    const lead = standings.find((s) => s.bet.id === 'total') ?? standings[0]
    let narration: string
    if (outcome === null) {
      narration = `${ordinal(position)} — not scored`
    } else if (winnerName === null) {
      narration = `${ordinal(position)} halved`
    } else {
      narration = `${winnerName} wins the ${ordinal(position)}`
    }
    if (lead && outcome !== null) {
      const u = lead.up
      narration +=
        u === 0
          ? ' — all square'
          : ` — ${u > 0 ? nameA : nameB} ${Math.abs(u)} up${
              lead.bet.id === 'total' && n >= 18 ? ' overall' : ''
            }`
    }

    steps.push({
      index: i,
      position,
      holeNo: hole.number,
      par: hole.par,
      strokeIndex: hole.strokeIndex,
      popsA: popsA[i] ?? 0,
      popsB: popsB[i] ?? 0,
      grossA: ga,
      grossB: gb,
      netA: na,
      netB: nb,
      outcome,
      standings,
      narration,
      offers,
    })
  }

  // An unanswered question is re-raised on every subsequent tee, since you are
  // still two down. Only the earliest one is actually outstanding: the walk
  // cannot get past it, and answering it changes every question after it.
  const firstPending = pending.length ? Math.min(...pending.map((o) => o.atIndex)) : -1
  const outstanding = pending.filter((o) => o.atIndex === firstPending)

  const standings = bets.map((b) => standingOf(b))

  const ledger = standings.reduce<NassauLedger>(
    (acc, s) => {
      if (s.winner === null) return { ...acc, open: acc.open + 1 }
      if (s.winner === 0) return { ...acc, halved: acc.halved + 1 }
      if (s.winner > 0) return { ...acc, aBets: acc.aBets + 1, net: acc.net + amountPerBet }
      return { ...acc, bBets: acc.bBets + 1, net: acc.net - amountPerBet }
    },
    { aBets: 0, bBets: 0, halved: 0, open: 0, net: 0 },
  )

  return { bets, steps, standings, ledger, pending: outstanding, playedThrough }
}
